import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, MessageCircle, Phone } from "lucide-react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";
import { api, formatApiError } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { useOrders } from "../context/OrdersContext";
import { useCall } from "../context/CallContext";
import { Avatar } from "../components/Avatar";
import { ru } from "../locales/ru";

dayjs.extend(relativeTime);
dayjs.locale("ru");

const pillCls = {
  open: "bg-black text-white",
  accepted: "bg-[#34C759]/15 text-[#1a7f37]",
  completed: "bg-gray-100 text-gray-500",
  cancelled: "bg-gray-100 text-gray-400",
};

const btnBase =
  "h-10 px-4 rounded-full text-sm font-semibold active:scale-[0.96] transition-transform disabled:opacity-40";

export const OrderCard = ({ order, compact = false }) => {
  const { user } = useAuth();
  const { upsert } = useOrders();
  const { startCall } = useCall();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  if (!order) return null;

  const isDriver = user?.roles?.includes("driver");
  const mine = order.passenger_id === user?.id;
  const myAccepted = order.driver_id === user?.id;

  const act = async (path) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/${path}`);
      upsert(data);
      toast.success(ru.orders.actionDone[path]);
    } catch (err) {
      toast.error(formatApiError(err, ru.errors.generic));
    } finally {
      setBusy(false);
    }
  };

  const statusLabel =
    order.status === "cancelled" && order.cancel_reason
      ? `${ru.orders.status.cancelled} · ${ru.orders.reason[order.cancel_reason] || ""}`
      : ru.orders.status[order.status];

  return (
    <div
      data-testid={`order-card-${order.id}`}
      className={`rounded-2xl border border-gray-200 bg-white p-4 ${compact ? "" : "shadow-[0_1px_2px_rgba(0,0,0,0.04)]"}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 font-bold text-[15px] leading-tight flex-wrap">
            <span className="truncate max-w-[150px]">{order.pickup_text}</span>
            {order.destination_text && (
              <>
                <ArrowRight size={14} className="text-gray-400 shrink-0" />
                <span className="truncate max-w-[150px]">{order.destination_text}</span>
              </>
            )}
          </div>
          {order.comment && (
            <p className="text-sm text-gray-500 mt-1 break-words" data-testid={`order-comment-${order.id}`}>
              {order.comment}
            </p>
          )}
        </div>
        <span
          data-testid={`order-status-${order.id}`}
          className={`text-[11px] font-bold px-2.5 py-1 rounded-full shrink-0 ${pillCls[order.status]}`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
        <Avatar avatar={order.passenger?.avatar} name={order.passenger?.name} size={24} />
        <span className="font-semibold text-gray-700 truncate">{order.passenger?.name}</span>
        <span>·</span>
        <span>{dayjs(order.created_at).fromNow()}</span>
      </div>

      {order.driver && (
        <div className="flex items-center gap-2 bg-gray-50 rounded-xl p-2.5 mb-3" data-testid={`order-driver-${order.id}`}>
          <Avatar avatar={order.driver.avatar} name={order.driver.name} size={32} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold truncate">{order.driver.name}</p>
            {order.driver.car && (
              <p className="text-xs text-gray-500 truncate">
                {[order.driver.car.model, order.driver.car.color, order.driver.car.plate].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          {mine && order.status === "accepted" && (
            <button
              data-testid={`order-call-${order.id}`}
              onClick={() => startCall({ id: order.driver.id, name: order.driver.name, avatar: order.driver.avatar })}
              title={ru.calls.call}
              className="shrink-0 w-10 h-10 rounded-full bg-black text-white flex items-center justify-center active:scale-[0.94] transition-transform"
            >
              <Phone size={16} />
            </button>
          )}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {isDriver && !mine && order.status === "open" && (
          <button data-testid={`order-accept-${order.id}`} disabled={busy} onClick={() => act("accept")} className={`${btnBase} bg-black text-white`}>
            {ru.orders.accept}
          </button>
        )}
        {myAccepted && order.status === "accepted" && (
          <>
            <button data-testid={`order-complete-${order.id}`} disabled={busy} onClick={() => act("complete")} className={`${btnBase} bg-black text-white`}>
              {ru.orders.complete}
            </button>
            <button data-testid={`order-release-${order.id}`} disabled={busy} onClick={() => act("release")} className={`${btnBase} bg-gray-100`}>
              {ru.orders.release}
            </button>
          </>
        )}
        {mine && (order.status === "open" || order.status === "accepted") && (
          <button data-testid={`order-cancel-${order.id}`} disabled={busy} onClick={() => act("cancel")} className={`${btnBase} bg-gray-100`}>
            {ru.orders.cancel}
          </button>
        )}
        {order.status === "accepted" && (mine || myAccepted) && (
          <button
            data-testid={`order-write-${order.id}`}
            onClick={() => navigate(`/chats/u/${mine ? order.driver_id : order.passenger_id}`)}
            className={`${btnBase} bg-gray-100 flex items-center gap-1.5`}
          >
            <MessageCircle size={15} /> {ru.orders.write}
          </button>
        )}
      </div>
    </div>
  );
};
